import React, {useState, useEffect} from 'react';
import {Box, Button, Chip, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import {useTranslation} from 'react-i18next';
import {
    AssistantFeedbackRating,
    AssistantMessage as MessageType,
} from '../assistantMessages';
import AssistantFeedbackBox from './AssistantFeedbackBox';
import {useRevealedText} from './useRevealedText';

interface Props {
    message: MessageType;
    /** This question went to the support team rather than the AI — marked on
     *  the bubble so a mixed thread reads unambiguously. User messages only. */
    viaTeam?: boolean;
    /** Save a thumb rating + optional explanation against this message.
     *  Pass `rating: null` to clear an existing rating (undo). */
    onFeedback?: (
        rating: AssistantFeedbackRating | null,
        explanation?: string,
    ) => void;
    /** Remove this message from the conversation. */
    onDelete?: () => void;
    /** Re-run the AI to regenerate this assistant reply. */
    onRetry?: () => void;
    /** Save an edited user message and re-answer. */
    onEdit?: (text: string) => void;
    /** Offered under an answer the assistant couldn't find: hand this question to the support team. */
    onAskTeam?: () => void | Promise<void>;
    /** Keep the answer's action row on screen instead of revealing it on hover (the newest answer). */
    keepActionsVisible?: boolean;
    /** Reveal the content progressively (a freshly generated answer). */
    animate?: boolean;
    /** Fires on each reveal tick so the parent can follow-scroll. */
    onReveal?: () => void;
}

// Calm, on-brand feedback colours — forest green for positive, muted coral
// for negative, rather than loud stop-light red/green.
const POSITIVE_COLOR = '#388e3c';
const NEGATIVE_COLOR = '#e57373';

// KB screenshots are ingested as host-relative paths (/v3/public/ai/knowledge-base/image/…) so stored content
// isn't pinned to one environment's host — resolve them against this app's API origin at render time.
const resolveImageSrc = (src: string): string =>
    src.startsWith('/') && !src.startsWith('//')
        ? `${(process.env.REACT_APP_API_ENDPOINT ?? '').replace(/\/+$/, '')}${src}`
        : src;

// The assistant points at the Support Center constantly ("I cannot find this in the Support Center"),
// so every mention becomes a way in. Same destination as the sidebar entry.
const SUPPORT_CENTER_LABEL = 'Support Center';
export const SUPPORT_CENTER_URL =
    'https://crqlar.notion.site/Knowledge-Base-new-35f9e2a69b99804b8c9fd83f4f795e87';
const SUPPORT_CENTER_CLASS = 'support-center-link';

/**
 * Rewrites plain-text "Support Center" mentions into links. Runs on the parsed tree rather than the
 * markdown source so a mention inside code, an image alt, or an existing link is left alone — a regex
 * over the raw string would happily corrupt all three.
 */
const rehypeSupportCenterLink = () => (tree: any) => {
    const walk = (node: any) => {
        if (!Array.isArray(node.children)) return;
        if (node.tagName === 'a' || node.tagName === 'code' || node.tagName === 'pre') return;

        node.children = node.children.flatMap((child: any) => {
            if (child.type !== 'text') {
                walk(child);
                return [child];
            }
            const segments: string[] = String(child.value).split(SUPPORT_CENTER_LABEL);
            if (segments.length === 1) return [child];

            return segments.flatMap((segment, index) => {
                const nodes: any[] = [];
                if (index > 0) {
                    nodes.push({
                        type: 'element',
                        tagName: 'a',
                        properties: {href: SUPPORT_CENTER_URL, className: [SUPPORT_CENTER_CLASS]},
                        children: [{type: 'text', value: SUPPORT_CENTER_LABEL}],
                    });
                }
                if (segment) nodes.push({type: 'text', value: segment});
                return nodes;
            });
        });
    };

    walk(tree);
};

// Dashed underline under the words only — the launch icon sits outside it, so the rule doesn't
// run under the glyph — and sized in em so it tracks whatever text it lands in.
const SupportCenterLink: React.FC<{href?: string; children: React.ReactNode}> = ({href, children}) => (
    <Box
        component="a"
        href={href || SUPPORT_CENTER_URL}
        target="_blank"
        rel="noreferrer"
        // `&&` outweighs the bubbles' descendant `& a` rules, which would otherwise
        // re-underline this and repaint it in their own link colour.
        sx={{
            '&&': {
                color: 'inherit',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
            },
        }}
    >
        <Box
            component="span"
            sx={{
                borderBottom: '1px dashed currentColor',
                '&:hover': {borderBottomStyle: 'solid'},
            }}
        >
            {children}
        </Box>
        <LaunchIcon
            sx={{
                fontSize: '0.8em',
                ml: '3px',
                verticalAlign: 'middle',
                position: 'relative',
                top: '-0.05em',
            }}
        />
    </Box>
);

// Markdown is the only render path for message content; one helper is shared
// across the user / assistant / error variants, and the bubble styling differs.
// `linkSupportCenter` is opt-in so the user's own words are never rewritten.
const Markdown: React.FC<{children: string; linkSupportCenter?: boolean}> = ({
    children,
    linkSupportCenter,
}) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={linkSupportCenter ? [rehypeRaw, rehypeSupportCenterLink] : [rehypeRaw]}
        components={{
            a: ({children: kids, href, title, className}) => (
                className?.includes(SUPPORT_CENTER_CLASS)
                    ? <SupportCenterLink href={href}>{kids}</SupportCenterLink>
                    : <a href={href} title={title} target="_blank" rel="noreferrer">
                        {kids}
                    </a>
            ),
            img: ({src, alt, title}) => {
                if (!src) return null;
                const resolved = resolveImageSrc(src);
                return (
                    <a
                        href={resolved}
                        target="_blank"
                        rel="noreferrer"
                        title={title || alt || undefined}
                        style={{display: 'block'}}
                    >
                        <img
                            src={resolved}
                            alt={alt || ''}
                            loading="lazy"
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: 8,
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                margin: '0.5em 0',
                            }}
                        />
                    </a>
                );
            },
        }}
    >
        {children}
    </ReactMarkdown>
);

const MessageAttachments: React.FC<{
    attachments?: MessageType['attachments'];
    align?: 'start' | 'end';
}> = ({attachments, align = 'start'}) => {
    const {t} = useTranslation();

    if (!attachments?.length) return null;

    return (
        <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            justifyContent={align === 'end' ? 'flex-end' : 'flex-start'}
            sx={{mt: 0.75}}
        >
            {attachments.map((attachment, index) => {
                const label = attachment.fileName || attachment.url || t('Attachment');
                if (attachment.type === 'IMAGE' && attachment.url) {
                    return (
                        <a
                            key={`${attachment.url}-${index}`}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            title={label}
                            style={{display: 'block'}}
                        >
                            <img
                                src={attachment.url}
                                alt={label}
                                loading="lazy"
                                style={{
                                    display: 'block',
                                    width: 96,
                                    maxWidth: '100%',
                                    height: 72,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            />
                        </a>
                    );
                }

                return (
                    <Chip
                        key={`${label}-${index}`}
                        size="small"
                        component={attachment.url ? 'a' : 'div'}
                        clickable={!!attachment.url}
                        href={attachment.url}
                        target="_blank"
                        rel="noreferrer"
                        label={label}
                        sx={{maxWidth: 220}}
                    />
                );
            })}
        </Stack>
    );
};

// Base markdown style applied to whichever Box renders the content; each
// variant overrides code/link colours to stay readable on its background.
const baseMarkdownSx = {
    fontSize: 14,
    lineHeight: 1.5,
    '& p': {m: 0, fontSize: 14, lineHeight: 1.5},
    '& p + p': {mt: 1},
    '& code': {
        bgcolor: '#f4f4f6',
        px: 0.5,
        py: 0.125,
        borderRadius: 0.5,
        fontSize: '0.875em',
        fontFamily: 'monospace',
    },
    '& pre': {
        bgcolor: '#f4f4f6',
        p: 1,
        borderRadius: 1,
        overflowX: 'auto',
        fontSize: '0.875em',
        '& code': {bgcolor: 'transparent', p: 0},
    },
    '& ul, & ol': {pl: 2.5, m: 0, '& + p': {mt: 1}},
    '& li': {fontSize: 14, lineHeight: 1.5},
    '& blockquote': {
        m: 0,
        pl: 1.5,
        borderLeft: '3px solid #e0e0e0',
        color: 'text.secondary',
    },
} as const;

// Handoff state is a property of the conversation now, shown once above the composer
// (AssistantHandoffBanner). Messages carry no waiting state: pre-rewrite threads may still hold a
// WAITING_FOR_HUMAN placeholder message, which simply renders as the text it stored.

const TEAM_REPLY_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/ms-icon-310x310.png`;

// A human wrote it: the same green the widget already uses for positive things, as a tinted bubble
// mirroring the admin portal's treatment of team replies.
const TEAM_BUBBLE_BG = 'rgba(56, 142, 60, 0.08)';
const TEAM_BUBBLE_BORDER = 'rgba(56, 142, 60, 0.28)';

const isTeamReply = (message: MessageType) =>
    message.origin === 'ADMIN' || message.origin === 'SLACK';

/**
 * Time for a message sent today, date + time otherwise, and the year as well once it isn't this one —
 * enough to place a reply that arrived while the customer was away, without a full timestamp on every
 * line. Locale-formatted, so it follows the interface language.
 */
const formatMessageMoment = (value: Date | string | undefined, locale: string): string | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;

    const time = date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    if (sameDay) return time;

    const day = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(date.getFullYear() === now.getFullYear() ? {} : {year: 'numeric'}),
    });
    return `${day} · ${time}`;
};

/**
 * Who answered and when. The team answers as the team: a reply is from Crqlar, never from a named
 * colleague — the customer gets one counterpart, and no individual's name is handed out.
 */
const MessageHeader: React.FC<{message: MessageType}> = ({message: m}) => {
    const {t, i18n} = useTranslation();
    const name = isTeamReply(m) ? t('Crqlar team') : t('Crqlar Assistant');
    const moment = formatMessageMoment(m.insertDate, i18n.language);

    return (
        <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{mb: 0.5, flexWrap: 'wrap'}}>
            <Typography sx={{fontSize: 12.5, fontWeight: 600, color: 'text.primary'}}>
                {name}
            </Typography>
            {moment && (
                <Typography sx={{fontSize: 11.5, color: 'text.disabled'}}>
                    · {moment}
                </Typography>
            )}
        </Stack>
    );
};

// Copy-to-clipboard button; flips to a green check for ~3.5s after a copy.
const CopyButton: React.FC<{text: string}> = ({text}) => {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleClick = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 3500);
        } catch {
            // clipboard write can fail in non-secure contexts; stay silent
        }
    };

    return (
        <Tooltip title={copied ? t('Copied!') : t('Copy')}>
            <IconButton
                size="small"
                onClick={handleClick}
                aria-label={copied ? t('Copied') : t('Copy message')}
            >
                {copied
                    ? <CheckIcon sx={{fontSize: 16, color: POSITIVE_COLOR}}/>
                    : <ContentCopyIcon sx={{fontSize: 15}}/>}
            </IconButton>
        </Tooltip>
    );
};

/**
 * ⋮ → Delete, on every message the customer can remove — their own questions, AI answers and the
 * team's replies alike (the backend allows any message in one's own thread).
 *
 * `alsoInSlack` softens the wording for anything the support team also holds: their reply, or a
 * question already forwarded to them. Deleting clears the customer's transcript, not the Slack
 * thread, and the label shouldn't promise otherwise.
 */
const DeleteMenuButton: React.FC<{
    onDelete: () => void;
    alsoInSlack?: boolean;
    align?: 'left' | 'right';
}> = ({onDelete, alsoInSlack, align = 'left'}) => {
    const {t} = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    return (
        <>
            <Tooltip title={t('More')}>
                <IconButton
                    size="small"
                    onClick={e => setMenuAnchor(e.currentTarget)}
                    aria-label={t('More options')}
                >
                    <MoreVertIcon sx={{fontSize: 16}}/>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{horizontal: align, vertical: 'top'}}
                anchorOrigin={{horizontal: align, vertical: 'bottom'}}
            >
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onDelete();
                    }}
                    sx={{fontSize: 14}}
                >
                    <DeleteOutlineIcon
                        fontSize="small"
                        sx={{mr: 1, color: 'text.secondary'}}
                    />
                    {alsoInSlack ? t('Remove from this chat') : t('Delete')}
                </MenuItem>
            </Menu>
        </>
    );
};

const AssistantRoleMarker: React.FC<{message: MessageType}> = ({message}) => {
    const {t} = useTranslation();
    const fromTeam = isTeamReply(message);

    return (
        <Tooltip title={fromTeam ? t('Team response') : t('AI response')}>
            <Box
                role="img"
                aria-label={fromTeam ? t('Team response') : t('AI response')}
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.25,
                    color: fromTeam ? POSITIVE_COLOR : 'primary.main',
                    bgcolor: fromTeam ? '#fff' : 'rgba(25, 118, 210, 0.08)',
                    border: '1px solid',
                    borderColor: fromTeam ? TEAM_BUBBLE_BORDER : 'rgba(25, 118, 210, 0.16)',
                    flexShrink: 0,
                    overflow: 'hidden',
                }}
            >
                {fromTeam
                    ? (
                        <Box
                            component="img"
                            src={TEAM_REPLY_AVATAR_SRC}
                            alt=""
                            aria-hidden
                            sx={{
                                display: 'block',
                                width: 22,
                                height: 22,
                                objectFit: 'contain',
                            }}
                        />
                    )
                    : <SmartToyOutlinedIcon sx={{fontSize: 17}}/>}
            </Box>
        </Tooltip>
    );
};

// ---- User message: right-aligned bubble, sharp top-right corner ------------

const UserMessageView: React.FC<{
    message: MessageType;
    onEdit?: (text: string) => void;
    onDelete?: () => void;
    viaTeam?: boolean;
}> = ({message: m, onEdit, onDelete, viaTeam}) => {
    const {t} = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(m.content);

    const cancelEdit = () => {
        setDraft(m.content);
        setIsEditing(false);
    };
    const submitEdit = () => {
        const trimmed = draft.trim();
        if (!trimmed || trimmed === m.content.trim()) return;
        onEdit?.(trimmed);
        setIsEditing(false);
    };
    const canSaveEdit = draft.trim().length > 0 && draft.trim() !== m.content.trim();

    // Edit mode: swap the bubble + actions for a full-width textarea with
    // Cancel/Update, in the spirit of Gemini's edit affordance.
    if (isEditing) {
        return (
            <Box sx={{width: '100%'}}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    multiline
                    minRows={3}
                    autoFocus
                    fullWidth
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitEdit();
                        } else if (e.key === 'Escape') {
                            cancelEdit();
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: 'background.paper',
                        },
                    }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{mt: 1}}>
                    <Button onClick={cancelEdit} size="small">
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={submitEdit}
                        size="small"
                        variant="contained"
                        disabled={!canSaveEdit}
                    >
                        {t('Update')}
                    </Button>
                </Stack>
            </Box>
        );
    }

    // The wrapper spans the full chat width so a hover anywhere beside the
    // bubble still reveals the action row; the bubble keeps maxWidth: 85% so
    // it stays right-aligned and chat-shaped.
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                width: '100%',
                '@media (hover: hover)': {
                    '& .user-msg-actions': {
                        opacity: 0,
                        transition: 'opacity 120ms ease',
                    },
                    '&:hover .user-msg-actions, &:focus-within .user-msg-actions': {
                        opacity: 1,
                    },
                },
            }}
        >
            <Box sx={{maxWidth: '85%'}}>
                {viaTeam && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.5}
                        sx={{mb: 0.5, mr: 0.25, color: '#ed6c02'}}
                    >
                        <SupportAgentOutlinedIcon sx={{fontSize: 13}}/>
                        <Typography sx={{fontSize: 11, fontWeight: 600}}>
                            {t('Sent to the team')}
                        </Typography>
                    </Stack>
                )}
                <Box
                    sx={{
                        px: 1.75,
                        py: 1.25,
                        // sharp at top-right for the chat-bubble look
                        borderRadius: '16px 4px 16px 16px',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        ...baseMarkdownSx,
                        // keep code blocks + links readable on the brand background
                        '& code': {
                            ...baseMarkdownSx['& code'],
                            bgcolor: 'rgba(255, 255, 255, 0.18)',
                            color: 'inherit',
                        },
                        '& pre': {
                            ...baseMarkdownSx['& pre'],
                            bgcolor: 'rgba(255, 255, 255, 0.12)',
                            color: 'inherit',
                        },
                        '& a': {color: 'inherit', textDecoration: 'underline'},
                        '& blockquote': {
                            ...baseMarkdownSx['& blockquote'],
                            borderLeftColor: 'rgba(255, 255, 255, 0.4)',
                            color: 'inherit',
                        },
                    }}
                >
                    <Markdown>{m.content}</Markdown>
                </Box>
                <MessageAttachments attachments={m.attachments} align="end"/>
            </Box>

            <Stack
                className="user-msg-actions"
                direction="row"
                justifyContent="flex-end"
                sx={{mt: 0.75, color: 'text.secondary'}}
            >
                <CopyButton text={m.content}/>

                {onEdit && (
                    <Tooltip title={t('Edit message')}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setDraft(m.content);
                                setIsEditing(true);
                            }}
                            aria-label={t('Edit message')}
                        >
                            <EditOutlinedIcon sx={{fontSize: 16}}/>
                        </IconButton>
                    </Tooltip>
                )}

                {onDelete && (
                    <DeleteMenuButton onDelete={onDelete} alsoInSlack={viaTeam} align="right"/>
                )}
            </Stack>
        </Box>
    );
};

// ---- Error: no bubble, red text --------------------------------------------

const ErrorMessageView: React.FC<{message: MessageType}> = ({message: m}) => (
    <Box
        sx={{
            alignSelf: 'flex-start',
            maxWidth: '100%',
            color: '#c62828',
            ...baseMarkdownSx,
            '& a': {color: 'inherit', textDecoration: 'underline'},
        }}
    >
        <Markdown>{m.content}</Markdown>
    </Box>
);

// ---- Assistant reply: AI plain, team replies in a green bubble, + actions row ----

const AssistantReplyView: React.FC<{
    message: MessageType;
    onFeedback?: (rating: AssistantFeedbackRating | null, explanation?: string) => void;
    onDelete?: () => void;
    onRetry?: () => void;
    onAskTeam?: () => void | Promise<void>;
    keepActionsVisible?: boolean;
    animate?: boolean;
    onReveal?: () => void;
}> = ({message: m, onFeedback, onDelete, onRetry, onAskTeam, keepActionsVisible, animate, onReveal}) => {
    const {t} = useTranslation();
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [askingTeam, setAskingTeam] = useState(false);

    // Client-side "streaming": reveal a freshly generated answer progressively.
    const shownContent = useRevealedText(m.content, !!animate);
    useEffect(() => {
        if (animate) onReveal?.();
    }, [shownContent, animate, onReveal]);

    const currentRating = m.feedback?.rating;

    const handleAskTeam = async () => {
        if (!onAskTeam || askingTeam) return;
        setAskingTeam(true);
        try {
            await onAskTeam();
        } finally {
            setAskingTeam(false);
        }
    };

    const handleThumb = (rating: AssistantFeedbackRating) => {
        // Tap to set, tap the same thumb again to clear (and hide the panel).
        if (currentRating === rating) {
            onFeedback?.(null);
            setFeedbackOpen(false);
            return;
        }
        onFeedback?.(rating);
        setFeedbackOpen(true);
    };

    const handleSubmitExplanation = (explanation: string) => {
        if (!currentRating) return;
        onFeedback?.(currentRating, explanation);
        setFeedbackOpen(false);
    };

    const fromTeam = isTeamReply(m);

    return (
        <Box
            sx={{
                alignSelf: 'stretch',
                maxWidth: '100%',
                display: 'grid',
                gridTemplateColumns: '28px minmax(0, 1fr)',
                columnGap: 1.25,
                // Older answers keep their actions until pointed at, so a long transcript reads as
                // text rather than as rows of buttons. Scoped to real pointers: where hover doesn't
                // exist (touch), the rule never applies and the row simply stays visible. Fading in
                // place rather than mounting on hover means no reflow either way.
                ...(keepActionsVisible ? {} : {
                    '@media (hover: hover)': {
                        '& .assistant-msg-actions': {
                            opacity: 0,
                            transition: 'opacity 120ms ease',
                        },
                        '&:hover .assistant-msg-actions, &:focus-within .assistant-msg-actions': {
                            opacity: 1,
                        },
                    },
                }),
            }}
        >
            <AssistantRoleMarker message={m}/>
            <Box sx={{minWidth: 0}}>
            <MessageHeader message={m}/>
            <Box
                sx={{
                    minWidth: 0,
                    color: 'text.primary',
                    ...baseMarkdownSx,
                    '& a': {color: 'primary.main', textDecoration: 'underline'},
                    // A human wrote this — say so with the surface itself, mirroring the user's own
                    // bubble (same corner language, mirrored) so the thread reads as a conversation
                    // between two people with the assistant's plain text in between.
                    ...(fromTeam ? {
                        px: 1.75,
                        py: 1.25,
                        borderRadius: '4px 16px 16px 16px',
                        bgcolor: TEAM_BUBBLE_BG,
                        border: `1px solid ${TEAM_BUBBLE_BORDER}`,
                        width: 'fit-content',
                        maxWidth: '100%',
                    } : {}),
                }}
            >
                <Markdown linkSupportCenter>
                    {shownContent}
                </Markdown>
                <MessageAttachments attachments={m.attachments}/>
            </Box>

            {/* Source links stay outside the markdown body so answer text and citations
                remain visually separate. */}
            {!!m.sources?.length && (
                <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                    sx={{mt: 1}}
                >
                    <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary'}}>
                        {t('Sources')}
                    </Typography>
                    {m.sources.map((source, index) => {
                        const sourceSx = {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: '100%',
                            minWidth: 0,
                            px: 0.75,
                            py: 0.375,
                            borderRadius: 1,
                            textDecoration: 'none',
                            color: 'text.secondary',
                            bgcolor: '#f7f8fa',
                            border: '1px solid #eceff1',
                            transition: 'color 120ms ease, border-color 120ms ease, background-color 120ms ease',
                            '&:hover': {
                                color: source.url ? 'primary.main' : 'text.secondary',
                                borderColor: source.url ? 'rgba(25, 118, 210, 0.24)' : '#eceff1',
                                bgcolor: source.url ? 'rgba(25, 118, 210, 0.06)' : '#f7f8fa',
                            },
                        } as const;
                        const inner = (
                            <>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {source.title}
                                </Typography>
                                {!!source.url && <OpenInNewIcon sx={{fontSize: 13, flexShrink: 0}}/>}
                            </>
                        );
                        return source.url ? (
                            <Box
                                key={`${source.url}-${index}`}
                                component="a"
                                href={source.url}
                                target="_blank"
                                rel="noreferrer"
                                title={source.title}
                                sx={sourceSx}
                            >
                                {inner}
                            </Box>
                        ) : (
                            <Box key={`${source.title}-${index}`} title={source.title} sx={sourceSx}>
                                {inner}
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {/* actions row */}
            <Stack
                className="assistant-msg-actions"
                direction="row"
                spacing={0.25}
                sx={{mt: 0.75, color: 'text.secondary'}}
            >
                <Tooltip
                    title={
                        currentRating === 'POSITIVE'
                            ? t('Helpful · click again to undo')
                            : t('Helpful')
                    }
                >
                    <IconButton
                        size="small"
                        onClick={() => handleThumb('POSITIVE')}
                        aria-label={t('Helpful')}
                        aria-pressed={currentRating === 'POSITIVE'}
                        sx={{
                            color: currentRating === 'POSITIVE'
                                ? POSITIVE_COLOR
                                : 'inherit',
                            '&:hover': {
                                color: POSITIVE_COLOR,
                                bgcolor: 'rgba(56, 142, 60, 0.08)',
                            },
                        }}
                    >
                        {currentRating === 'POSITIVE' ? (
                            <ThumbUpIcon sx={{fontSize: 16}}/>
                        ) : (
                            <ThumbUpOutlinedIcon sx={{fontSize: 16}}/>
                        )}
                    </IconButton>
                </Tooltip>

                <Tooltip
                    title={
                        currentRating === 'NEGATIVE'
                            ? t('Not helpful · click again to undo')
                            : t('Not helpful')
                    }
                >
                    <IconButton
                        size="small"
                        onClick={() => handleThumb('NEGATIVE')}
                        aria-label={t('Not helpful')}
                        aria-pressed={currentRating === 'NEGATIVE'}
                        sx={{
                            color: currentRating === 'NEGATIVE'
                                ? NEGATIVE_COLOR
                                : 'inherit',
                            '&:hover': {
                                color: NEGATIVE_COLOR,
                                bgcolor: 'rgba(229, 115, 115, 0.08)',
                            },
                        }}
                    >
                        {currentRating === 'NEGATIVE' ? (
                            <ThumbDownIcon sx={{fontSize: 16}}/>
                        ) : (
                            <ThumbDownOutlinedIcon sx={{fontSize: 16}}/>
                        )}
                    </IconButton>
                </Tooltip>

                <CopyButton text={m.content}/>

                {onRetry && (
                    <Tooltip title={t('Regenerate response')}>
                        <IconButton
                            size="small"
                            onClick={onRetry}
                            aria-label={t('Regenerate response')}
                        >
                            <RefreshIcon sx={{fontSize: 16}}/>
                        </IconButton>
                    </Tooltip>
                )}

                {onDelete && (
                    <DeleteMenuButton
                        onDelete={onDelete}
                        alsoInSlack={m.origin === 'ADMIN' || m.origin === 'SLACK'}
                    />
                )}

                {/* The assistant came up empty — offer the people who won't. Handing over sends no
                    new message: the team picks up the question that's already in the thread. */}
                {onAskTeam && (
                    <Button
                        size="small"
                        onClick={handleAskTeam}
                        disabled={askingTeam}
                        startIcon={<SupportAgentOutlinedIcon sx={{fontSize: 15}}/>}
                        sx={{
                            ml: 0.5,
                            px: 1,
                            minWidth: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'none',
                            color: '#ed6c02',
                            '&:hover': {bgcolor: 'rgba(237, 108, 2, 0.08)'},
                        }}
                    >
                        {t('Ask the team')}
                    </Button>
                )}
            </Stack>

            {/* inline feedback panel — only after a thumb is clicked */}
            {feedbackOpen && currentRating && (
                <AssistantFeedbackBox
                    initialExplanation={m.feedback?.explanation}
                    onSubmit={handleSubmitExplanation}
                    onClose={() => setFeedbackOpen(false)}
                />
            )}

            </Box>
        </Box>
    );
};

const AssistantMessage: React.FC<Props> = ({message, onFeedback, onDelete, onRetry, onEdit, onAskTeam, keepActionsVisible, animate, onReveal, viaTeam}) => {
    if (message.role === 'user') {
        return (
            <UserMessageView
                message={message}
                onEdit={onEdit}
                onDelete={onDelete}
                viaTeam={viaTeam}
            />
        );
    }
    if (message.isError) {
        return <ErrorMessageView message={message}/>;
    }
    return (
        <AssistantReplyView
            message={message}
            onFeedback={onFeedback}
            onDelete={onDelete}
            onRetry={onRetry}
            onAskTeam={onAskTeam}
            keepActionsVisible={keepActionsVisible}
            animate={animate}
            onReveal={onReveal}
        />
    );
};

export default AssistantMessage;
